import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../interceptors";

const LS_KEY = "ample_job_scope";

const loadScope = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveScope = (ids) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(ids));
  } catch {
    /* storage disabled */
  }
};

/* ── Fetch the org's published-jobs list once and cache in store ── */
export const fetchJobsForScope = createAsyncThunk(
  "jobScope/fetchJobs",
  async (_arg, { rejectWithValue }) => {
    try {
      const userId = localStorage.getItem("userId") || "1";
      const r = await axiosInstance.post(
        "/job-posting/job-details/get-all-published-jobs",
        { userId, start: "0", length: "100" }
      );
      const list = r.data?.list || [];
      return list.map((j) => ({
        id: j.jobId,
        name: j.positionName,
        location: (j.locations || []).join(", "),
        openings: j.positionCounts || 0,
        status: j.status || "OPEN",
      }));
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to load jobs");
    }
  }
);

const jobScopeSlice = createSlice({
  name: "jobScope",
  initialState: {
    selectedJobIds: loadScope(),
    dropdownOpen: false,
    jobs: [],
    jobsLoaded: false,
    jobsLoading: false,
    jobsError: null,
    search: "",
  },
  reducers: {
    toggleJob(state, { payload: jobId }) {
      const i = state.selectedJobIds.indexOf(jobId);
      if (i >= 0) state.selectedJobIds.splice(i, 1);
      else state.selectedJobIds.push(jobId);
      saveScope(state.selectedJobIds);
    },
    setScope(state, { payload: jobIds }) {
      state.selectedJobIds = jobIds;
      saveScope(jobIds);
    },
    selectAll(state, { payload: allJobIds }) {
      state.selectedJobIds = allJobIds;
      saveScope(allJobIds);
    },
    clearScope(state) {
      state.selectedJobIds = [];
      saveScope([]);
    },
    toggleDropdown(state) {
      state.dropdownOpen = !state.dropdownOpen;
    },
    openDropdown(state) {
      state.dropdownOpen = true;
    },
    closeDropdown(state) {
      state.dropdownOpen = false;
    },
    setScopeSearch(state, { payload }) {
      state.search = payload || "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobsForScope.pending, (state) => {
        state.jobsLoading = true;
        state.jobsError = null;
      })
      .addCase(fetchJobsForScope.fulfilled, (state, { payload }) => {
        state.jobs = payload;
        state.jobsLoaded = true;
        state.jobsLoading = false;
        // Drop any persisted scope IDs that no longer exist
        const valid = new Set(payload.map((j) => j.id));
        const filtered = state.selectedJobIds.filter((id) => valid.has(id));
        if (filtered.length !== state.selectedJobIds.length) {
          state.selectedJobIds = filtered;
          saveScope(filtered);
        }
      })
      .addCase(fetchJobsForScope.rejected, (state, { payload }) => {
        state.jobsLoading = false;
        state.jobsLoaded = true;
        state.jobsError = payload || "Failed to load jobs";
      });
  },
});

export const {
  toggleJob,
  setScope,
  selectAll,
  clearScope,
  toggleDropdown,
  openDropdown,
  closeDropdown,
  setScopeSearch,
} = jobScopeSlice.actions;

export const selectScopedJobIds = (s) => s.jobScopeSliceReducer.selectedJobIds;
export const selectIsScoped = (s) =>
  s.jobScopeSliceReducer.selectedJobIds.length > 0;
export const selectScopeDropdownOpen = (s) => s.jobScopeSliceReducer.dropdownOpen;
export const selectScopeJobs = (s) => s.jobScopeSliceReducer.jobs;
export const selectScopeJobsLoading = (s) => s.jobScopeSliceReducer.jobsLoading;
export const selectScopeJobsLoaded = (s) => s.jobScopeSliceReducer.jobsLoaded;
export const selectScopeSearch = (s) => s.jobScopeSliceReducer.search;
export const selectScopedJobs = (s) => {
  const ids = new Set(s.jobScopeSliceReducer.selectedJobIds);
  return s.jobScopeSliceReducer.jobs.filter((j) => ids.has(j.id));
};

export default jobScopeSlice.reducer;
